import { notifyAuthExpired } from './auth/authEvents.js';
import { clearAuthSession, readAuthSession, saveAuthSession } from './auth/authStorage.js';
import { isTokenExpired } from './auth/token.js';

const API_BASE_URL = (import.meta.env?.VITE_API_URL || 'https://d33c4ef3r7zp4c.cloudfront.net/').replace(/\/$/, '');

export class ApiError extends Error {
  constructor(message, { status = null, detail = '' } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

const readResponseBody = async (response) => {
  const responseText = await response.text();
  if (!responseText) return null;

  try {
    return JSON.parse(responseText);
  } catch {
    return responseText.slice(0, 300);
  }
};

const getResponseDetail = (body) => {
  if (typeof body === 'string') return body;
  const detail = body?.body?.message || body?.body?.error || body?.message || body?.error || '';
  return typeof detail === 'string' ? detail : JSON.stringify(detail);
};

const createApiError = (fallbackMessage, response, body) => {
  const detail = getResponseDetail(body);
  const detailMessage = detail ? `: ${detail}` : '';
  return new ApiError(`${fallbackMessage} (${response.status})${detailMessage}`, {
    status: response.status,
    detail,
  });
};

const parseJsonResponse = async (response, fallbackMessage) => {
  const body = await readResponseBody(response);
  if (!response.ok) throw createApiError(fallbackMessage, response, body);
  return body;
};

let refreshPromise = null;

export const refreshAuthSession = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const session = readAuthSession();
    if (!session?.refreshToken || !session?.username) {
      throw new ApiError('Missing refresh credentials', { status: 401 });
    }

    const response = await fetch(`${API_BASE_URL}/customer/refresh-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: session.username,
        refreshToken: session.refreshToken,
      }),
    });
    const body = await parseJsonResponse(response, 'Failed to refresh session');
    const idToken = body?.idToken || body?.data?.idToken;
    if (!idToken || isTokenExpired(idToken)) {
      throw new ApiError('The refreshed session is invalid', { status: 401 });
    }

    return saveAuthSession({
      ...session,
      idToken,
      accessToken: body?.accessToken || body?.data?.accessToken || session.accessToken,
      source: 'credentials',
    });
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

const expireAuthentication = () => {
  clearAuthSession();
  notifyAuthExpired();
};

const getRequestSession = async () => {
  const session = readAuthSession();
  if (!session?.idToken) throw new ApiError('Authentication is required', { status: 401 });
  if (!isTokenExpired(session.idToken)) return session;

  try {
    return await refreshAuthSession();
  } catch (error) {
    expireAuthentication();
    throw error;
  }
};

const requestJson = async (path, {
  body = {},
  signal,
  requiresAuth = true,
  retryAfterRefresh = true,
  fallbackMessage = 'Request failed',
} = {}) => {
  const session = requiresAuth ? await getRequestSession() : null;
  const headers = { 'Content-Type': 'application/json' };
  if (session?.idToken) headers.Authorization = `Bearer ${session.idToken}`;

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal,
  });

  if (response.status === 401 && requiresAuth && retryAfterRefresh) {
    try {
      await refreshAuthSession();
      return requestJson(path, {
        body,
        signal,
        requiresAuth,
        retryAfterRefresh: false,
        fallbackMessage,
      });
    } catch (error) {
      expireAuthentication();
      throw error;
    }
  }

  if (response.status === 401 && requiresAuth) {
    expireAuthentication();
  }

  return parseJsonResponse(response, fallbackMessage);
};

export const exchangeWebNonce = (nonce) => requestJson('/customer/exchange-web-nonce', {
  body: { nonce },
  requiresAuth: false,
  fallbackMessage: 'Failed to exchange web nonce',
});

export const loginWithCredentials = (username, password) => requestJson('/customer/login', {
  body: { username, password },
  requiresAuth: false,
  fallbackMessage: 'Login failed',
});

export const verifyLoginMfa = ({ username, session, code }) => requestJson('/customer/vendor-respond-totp', {
  body: { username, session, user_code: code },
  requiresAuth: false,
  fallbackMessage: 'Verification failed',
});

export const fetchOverview = ({ signal } = {}) => requestJson('/customer/billing/overview', {
  body: {},
  signal,
  fallbackMessage: 'Failed to fetch overview',
});

export const fetchPlans = ({ signal } = {}) => requestJson('/customer/billing/plans', {
  body: {},
  signal,
  fallbackMessage: 'Failed to fetch plans',
});

export const createCheckoutSession = (plan_id, frequency = 'monthly') => requestJson('/customer/billing/checkout', {
  body: { plan_id, frequency },
  fallbackMessage: 'Failed to create checkout session',
});

export const fetchVendorCredits = () => requestJson('/customer/billing/vendor-credits', {
  body: {},
  fallbackMessage: 'Failed to fetch vendor credits',
});

export const requestVendorCredits = (vendor_id, amount, reason) => {
  const body = { vendor_id, reason };
  if (amount !== undefined && amount !== null && amount !== 0) body.amount = parseInt(amount, 10);
  return requestJson('/customer/billing/request-credits', {
    body,
    fallbackMessage: 'Failed to request credits',
  });
};

export const fetchCreditRequests = () => requestJson('/customer/billing/requests', {
  body: {},
  fallbackMessage: 'Failed to fetch credit requests',
});

export const fetchTransactions = () => requestJson('/customer/billing/transactions', {
  body: {},
  fallbackMessage: 'Failed to fetch transactions',
});

export const upgradeSubscription = (plan_id, frequency = 'monthly') => requestJson('/customer/billing/upgrade', {
  body: { plan_id, frequency },
  fallbackMessage: 'Failed to upgrade subscription',
});

export const downgradeSubscription = (plan_id, frequency = 'monthly') => requestJson('/customer/billing/downgrade', {
  body: { plan_id, frequency },
  fallbackMessage: 'Failed to downgrade subscription',
});

export const cancelSubscription = () => requestJson('/customer/billing/cancel', {
  body: {},
  fallbackMessage: 'Failed to cancel subscription',
});

export const createPassCheckoutSession = () => requestJson('/customer/billing/checkout-pass', {
  body: {},
  fallbackMessage: 'Failed to create pass checkout session',
});

export const fetchCreditHistory = (limit = 50, exclusive_start_key = null) => requestJson('/customer/billing/credit-history', {
  body: { limit, exclusive_start_key },
  fallbackMessage: 'Failed to fetch credit history',
});

export const fetchVendorCreditDetail = (vendor_id) => requestJson('/customer/billing/vendor-credit-detail', {
  body: { vendor_id },
  fallbackMessage: 'Failed to fetch vendor credit detail',
});
